// Единый отклик на нажатия для всего приложения — без правок в каждом компоненте.
//  • .is-pressed на элементе, пока палец на нём (в WebView :active срабатывает
//    не всегда) — CSS слегка «вдавливает» элемент, при отпускании он пружинит
//    обратно; для мыши — .is-pressed-mouse: подсветка без сжатия;
//  • волна (ripple) от точки касания — рисуется в отдельном слое поверх
//    элемента, DOM компонентов не трогаем;
//  • aria-busy="true" на кнопке, пока идёт запрос, запущенный её нажатием
//    (CSS показывает «дыхание»), — видно, что нажатие принято;
//  • иконка «обновить» прокручивается при нажатии.
// На касаниях отклик появляется с задержкой 70 мс и отменяется, если палец
// поехал (это прокрутка, а не нажатие).
import { prefersReducedMotion } from './celebrate';

const CLICKABLE = [
    'button', 'a[href]', '[role="button"]', 'summary',
    '.hover', '.task_btn', '.btn', '.btn-follow', '.btn-follow_tiktok', '.send_btn', '.custom_task_btn',
    '.start_custom_task_btn', '.load_screen_btn', '.refresh_container', '.task_item_container',
    '.bottom_menu_item', '.nav__item', '.nav__item-selected', '.select__item', '.payout_item',
    '.payout_picker_img_cont', '.copy_container', '.copy_id_support', '.copy_review_text_img',
    '.edit_answer', '.appeal_text', '.appeal_text_bg', '.appeal_text_task_money', '.red_appeal_text',
    '.open_support_btn', '.telegram_bind_link', '.telegram_bind_btn', '.telegram_bind_banner',
    '.ref_btn_width', '.profile_ref_btn', '.profile_ref_bg_cont', '.error_btn', '.close_icon_error',
    '.change_acc_insta', '.center_btn', '.sign_in_btn', '.form__btn', '.sdk_appeal_btn',
    '.instr_btn', '.help_screen_icon', '.fine_ok', '.close_ios', '.sdk_title_controll',
    '.auth_promo_id_btn', '.arrows__item', '.arrows__item_next', '.arrow_item_follower', '.link_yandex'
].join(',');

// Кнопки-действия: на них показываем «занято», пока идёт запрос
const ACTIONS = [
    'button', '[role="button"]', '.task_btn', '.btn', '.btn-follow', '.btn-follow_tiktok', '.send_btn',
    '.custom_task_btn', '.start_custom_task_btn', '.load_screen_btn', '.open_support_btn', '.telegram_bind_btn',
    '.ref_btn_width', '.profile_ref_btn', '.error_btn', '.center_btn', '.sign_in_btn', '.form__btn',
    '.instr_btn', '.fine_ok', '.auth_promo_id_btn'
].join(',');

const SKIP = 'input, textarea, select, [contenteditable="true"], [contenteditable=""], .celebration, .press-layer';

const TOUCH_DELAY = 70;      // мс до показа отклика на касании
const MIN_PRESSED = 140;     // минимум, сколько держим «нажатое» состояние (быстрый тап)
const SLOP = 10;             // px — дальше считаем, что это прокрутка
const BUSY_WINDOW = 800;     // запрос в течение стольких мс после нажатия считаем «его»
const BUSY_MIN = 320;        // «занято» показываем не короче, чтобы не мигало
const BUSY_MAX = 15000;      // и не дольше

let installed = false;

function parseRgb(color) {
    const m = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+%?))?/.exec(color || '');
    if (!m) return null;
    let a = m[4] === undefined ? 1 : parseFloat(m[4]);
    if (m[4] && m[4].indexOf('%') > 0) a = a / 100;
    return { r: +m[1], g: +m[2], b: +m[3], a: a };
}

// Тёмный фон под элементом → светлая волна, светлый → тёмная
function isDarkSurface(el) {
    let node = el;
    for (let i = 0; node && node.nodeType === 1 && i < 5; i++) {
        let c = null;
        try { c = parseRgb(window.getComputedStyle(node).backgroundColor); } catch (e) { c = null; }
        if (c && c.a > 0.35) {
            const lum = (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
            return lum < 0.55;
        }
        node = node.parentElement;
    }
    return false;
}

function isWide(rect) {
    return rect.width > window.innerWidth * 0.55 || rect.height > 88;
}

export function installPressFeedback() {
    if (installed || typeof window === 'undefined' || !document || !document.addEventListener) return;
    if (!window.PointerEvent) return;          // очень старый WebView — оставляем как было
    installed = true;

    let supportsScale = false;
    try { supportsScale = !!(window.CSS && CSS.supports && CSS.supports('scale', '1')); } catch (e) { supportsScale = false; }

    let layer = null;
    const getLayer = () => {
        if (!layer || !layer.parentNode) {
            layer = document.createElement('div');
            layer.className = 'press-layer';
            layer.setAttribute('aria-hidden', 'true');
            document.body.appendChild(layer);
        }
        return layer;
    };

    const findClickable = (start) => {
        let target = start;
        if (target && target.nodeType !== 1) target = target.parentElement;
        if (!target || !target.closest) return null;
        if (target.closest(SKIP)) return null;
        let el = target.closest(CLICKABLE);
        if (!el) {
            // запасной вариант: элемент с курсором-«рукой» (кликабельные div без класса из списка).
            // cursor наследуется, поэтому берём верхний элемент непрерывной цепочки — тот,
            // от которого «рука» и пошла, а не вложенный span.
            let node = target;
            for (let depth = 0; node && node !== document.body && depth < 6; depth++) {
                let cursor = '';
                try { cursor = window.getComputedStyle(node).cursor; } catch (e) { cursor = ''; }
                if (cursor === 'pointer') el = node;
                else if (el) break;
                node = node.parentElement;
            }
        }
        if (!el || el === document.body || el === document.documentElement) return null;
        if (el.disabled || el.getAttribute('aria-disabled') === 'true' || (el.closest && el.closest('.not_ready'))) return null;
        const r = el.getBoundingClientRect();
        if (r.width < 4 || r.height < 4) return null;
        if (r.width * r.height > window.innerWidth * window.innerHeight * 0.6) return null;   // это контейнер страницы
        return el;
    };

    // sink — элемент «вдавливается» (касание), значит и волна вместе с ним
    const spawnRipple = (el, rect, px, py, wide, sink) => {
        const host = document.createElement('span');
        host.className = 'press-ripple-host' + (wide ? ' is-wide' : '');
        let left = rect.left;
        let top = rect.top;
        let w = rect.width;
        let h = rect.height;
        let x = px;
        let y = py;
        let radius = '0px';
        try { radius = window.getComputedStyle(el).borderRadius || '0px'; } catch (e) { radius = '0px'; }
        if (w < 56 && h < 56) {
            // маленькие иконки — круглая волна вокруг центра, как у иконок-кнопок Android
            const s = Math.max(40, Math.max(w, h) + 16);
            left = rect.left + w / 2 - s / 2;
            top = rect.top + h / 2 - s / 2;
            w = s;
            h = s;
            x = left + s / 2;
            y = top + s / 2;
            radius = '50%';
            host.className += ' is-round';
        }
        host.style.left = left + 'px';
        host.style.top = top + 'px';
        host.style.width = w + 'px';
        host.style.height = h + 'px';
        host.style.borderRadius = radius;
        const wave = document.createElement('span');
        wave.className = 'press-ripple ' + (isDarkSurface(el) ? 'press-ripple--light' : 'press-ripple--dark');
        const dx = Math.max(x - left, left + w - x);
        const dy = Math.max(y - top, top + h - y);
        const r = Math.sqrt(dx * dx + dy * dy);
        wave.style.width = (r * 2) + 'px';
        wave.style.height = (r * 2) + 'px';
        wave.style.left = (x - left - r) + 'px';
        wave.style.top = (y - top - r) + 'px';
        host.appendChild(wave);
        getLayer().appendChild(host);
        if (sink && supportsScale) {
            // волна «вдавливается» вместе с элементом
            window.requestAnimationFrame(() => host.classList.add('is-down'));
        }
        return host;
    };

    const removeHost = (host) => {
        if (host && host.parentNode) host.parentNode.removeChild(host);
    };

    const isConnected = (el) => (typeof el.isConnected === 'boolean' ? el.isConnected : document.body.contains(el));

    const fadeRipple = (host, fast, el) => {
        if (!host) return;
        host.classList.remove('is-down');
        host.classList.add(fast ? 'is-cancel' : 'is-out');
        const until = Date.now() + (fast ? 200 : 480);
        // если по нажатию открылся другой экран и элемента уже нет —
        // волна не должна «висеть» призраком поверх нового экрана
        const watch = () => {
            if (!host.parentNode) return;
            if (Date.now() >= until || (el && !isConnected(el))) {
                removeHost(host);
                return;
            }
            window.requestAnimationFrame(watch);
        };
        window.requestAnimationFrame(watch);
        window.setTimeout(() => removeHost(host), fast ? 220 : 500);   // страховка, если rAF стоит на паузе
    };

    let active = null;

    const show = (st) => {
        if (active !== st || st.done || st.shown) return;
        st.shown = true;
        st.shownAt = Date.now();
        const rect = st.el.getBoundingClientRect();
        const wide = isWide(rect);
        // Мышь: элемент не сжимаем — иначе клик у самого края «уезжает» с элемента
        // и onClick не срабатывает. На касаниях браузер подстраивает точку тапа
        // под ближайший кликабельный элемент, там «вдавливание» безопасно.
        const sink = st.type !== 'mouse';
        st.el.classList.add(sink ? 'is-pressed' : 'is-pressed-mouse');
        if (sink && wide) st.el.classList.add('is-pressed-wide');
        if (!prefersReducedMotion()) st.ripple = spawnRipple(st.el, rect, st.x, st.y, wide, sink);
    };

    const finish = (st, cancelled) => {
        if (!st || st.done) return;
        st.done = true;
        window.clearTimeout(st.timer);
        if (active === st) active = null;
        let released = false;
        const release = () => {
            if (released) return;
            released = true;
            st.el.classList.remove('is-pressed');
            st.el.classList.remove('is-pressed-wide');
            st.el.classList.remove('is-pressed-mouse');
            fadeRipple(st.ripple, cancelled, st.el);
        };
        const held = Date.now() - st.shownAt;
        if (!cancelled && st.shown && held < MIN_PRESSED) {
            // быстрый тап: держим «нажатие» ещё чуть-чуть, но отпускаем сразу,
            // если элемент исчез (по тапу открылся другой экран)
            const until = Date.now() + (MIN_PRESSED - held);
            const wait = () => {
                if (released) return;
                if (Date.now() >= until || !isConnected(st.el)) release();
                else window.requestAnimationFrame(wait);
            };
            window.requestAnimationFrame(wait);
            window.setTimeout(release, MIN_PRESSED - held + 50);
        } else {
            release();
        }
    };

    const onDown = (e) => {
        if (e.isPrimary === false) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (active) finish(active, true);
        const el = findClickable(e.target);
        if (!el) return;
        const st = { el: el, x: e.clientX, y: e.clientY, id: e.pointerId, type: e.pointerType, shown: false, shownAt: 0, done: false, timer: 0, ripple: null };
        active = st;
        if (e.pointerType === 'touch' || e.pointerType === 'pen') {
            st.timer = window.setTimeout(() => show(st), TOUCH_DELAY);
        } else {
            show(st);
        }
    };

    const onMove = (e) => {
        const st = active;
        if (!st || e.pointerId !== st.id) return;
        if (st.type === 'mouse') {
            const r = st.el.getBoundingClientRect();
            if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) finish(st, true);
        } else if (Math.abs(e.clientX - st.x) > SLOP || Math.abs(e.clientY - st.y) > SLOP) {
            finish(st, true);                 // палец поехал — это прокрутка
        }
    };

    const onUp = (e) => {
        const st = active;
        if (!st || (e.pointerId !== undefined && e.pointerId !== st.id)) return;
        if (!st.shown) {
            window.clearTimeout(st.timer);
            show(st);                         // быстрый тап — отклик всё равно мелькнёт
        }
        finish(st, false);
    };

    const cancelActive = () => {
        if (active) finish(active, true);
    };

    const opts = { capture: true, passive: true };
    document.addEventListener('pointerdown', onDown, opts);
    document.addEventListener('pointermove', onMove, opts);
    document.addEventListener('pointerup', onUp, opts);
    document.addEventListener('pointercancel', cancelActive, opts);
    window.addEventListener('scroll', cancelActive, opts);
    window.addEventListener('blur', cancelActive);
    document.addEventListener('visibilitychange', cancelActive);

    // ── «обновить» крутится, «занято» на кнопке, пока идёт её запрос ──
    let lastAction = null;
    const restartClass = (el, cls, ms) => {
        el.classList.remove(cls);
        void el.offsetWidth;                  // перезапуск CSS-анимации
        el.classList.add(cls);
        window.setTimeout(() => el.classList.remove(cls), ms);
    };
    document.addEventListener('click', (e) => {
        const t = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
        if (!t || !t.closest) return;
        const action = t.closest(ACTIONS);
        lastAction = action && !action.disabled ? { el: action, at: Date.now() } : null;
        const refresh = t.closest('.refresh_container');
        if (refresh) restartClass(refresh, 'is-spinning', 750);
    }, true);

    const busyCount = typeof WeakMap === 'function' ? new WeakMap() : null;
    const markBusy = (el, promise) => {
        if (!busyCount) return;
        const n = (busyCount.get(el) || 0) + 1;
        busyCount.set(el, n);
        if (n === 1) el.setAttribute('aria-busy', 'true');
        const started = Date.now();
        let settled = false;
        const done = () => {
            if (settled) return;
            settled = true;
            window.setTimeout(() => {
                const left = (busyCount.get(el) || 1) - 1;
                busyCount.set(el, left);
                if (left <= 0) el.removeAttribute('aria-busy');
            }, Math.max(0, BUSY_MIN - (Date.now() - started)));
        };
        promise.then(done, done);
        window.setTimeout(done, BUSY_MAX);
    };

    const nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function' && !nativeFetch.__pressWrapped) {
        const wrapped = function () {
            const p = nativeFetch.apply(window, arguments);
            try {
                const a = lastAction;
                if (a && Date.now() - a.at < BUSY_WINDOW && p && typeof p.then === 'function' &&
                    document.body.contains(a.el)) {
                    markBusy(a.el, p);
                }
            } catch (e) { /* отклик не должен ломать запросы */ }
            return p;
        };
        wrapped.__pressWrapped = true;
        window.fetch = wrapped;
    }
}
